import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import { Text } from '@nextiva/next-ui';
import { Button } from '@nextiva/next-ui';
import { TextField, InputAdornment } from '@material-ui/core';
import { Search, CalendarToday } from '@material-ui/icons';
import DayPicker, { DateUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css';

import './InvoicesFilter.scss';

import { doSearchDelayed, applyFiler } from 'utils';

export const blockName = 'invoices-filter';
const DATE_FORMAT = 'MM/DD/YYYY';

const InvoicesFilter = props => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [invoiceName, setInvoiceName] = useState('');
  const range = useMemo(() => {
    return {
      from: undefined,
      to: undefined,
    };
  }, []);
  const [dateRange, setDateRange] = useState(range);
  const dateMenuRef = useRef(null);

  const handleOpenModal = useCallback(() => {
    setIsMenuOpen(true);
  }, [setIsMenuOpen]);

  const handleCloseModal = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleOpenDatePicker = useCallback(() => {
    setIsDatePickerOpen(true);
    setDateRange(range);
  }, [range]);

  const handleCloseDatePicker = useCallback(() => {
    setIsDatePickerOpen(false);
  }, []);

  const checkMenuOpen = useCallback(
    event => {
      if (
        (dateMenuRef &&
          document.getElementById('date-menu')?.contains(event.target)) ||
        event.target.id === 'date'
      ) {
        setIsMenuOpen(true);
      } else {
        handleCloseDatePicker();
        handleCloseModal();
      }
    },
    [handleCloseModal, handleCloseDatePicker]
  );

  const {
    startAddInvoices,
    setInvoiceNameFilter,
    setDateFilter,
    addGlobalWarning,
    billToSiteNumber,
  } = props;

  const onInvoiceNameChange = useCallback(
    event => {
      const { value } = event.target;

      setInvoiceName(value);
      doSearchDelayed(() => {
        setInvoiceNameFilter({ invoiceName: value });
        startAddInvoices({ searchElement: value, billToSiteNumber });
      });
    },
    [billToSiteNumber, startAddInvoices, setInvoiceNameFilter]
  );

  const handleDateRange = useCallback(
    day => {
      setDateRange(DateUtils.addDayToRange(day, dateRange));
    },
    [dateRange]
  );

  const onDateRangeSelected = useCallback(
    event => {
      setInvoiceName('');
      const isValidFilter = applyFiler({
        id: event.target.id,
        setDateFilter,
        startAddInvoices,
        setIsMenuOpen,
        startDate: moment(dateRange.from).format('YYYY-MM-DD'),
        toDate: moment(dateRange.to).format('YYYY-MM-DD'),
        setDateRange,
        handleCloseDatePicker,
        isDatePickerOpen,
        billToSiteNumber,
      });

      if (!isValidFilter) {
        addGlobalWarning('Time difference should not be greater than one year');
      }
    },
    [
      addGlobalWarning,
      dateRange.from,
      dateRange.to,
      handleCloseDatePicker,
      isDatePickerOpen,
      setDateFilter,
      startAddInvoices,
      billToSiteNumber,
    ]
  );

  useEffect(() => {
    window.addEventListener('click', checkMenuOpen);
    return () => {
      window.removeEventListener('click', checkMenuOpen);
    };
  }, [checkMenuOpen, handleCloseModal]);

  const dateRangeField = `${
    dateRange.from
      ? moment(dateRange.from).format(DATE_FORMAT)
      : moment()
          .subtract(3, 'month')
          .format(DATE_FORMAT)
  } - ${
    dateRange.to
      ? moment(dateRange.to).format(DATE_FORMAT)
      : moment().format(DATE_FORMAT)
  }`;

  return (
    <article className={blockName} data-testid={blockName}>
      <Text kind="h1" className={`${blockName}__title-text`}>
        Transactions
      </Text>
      <div className={`${blockName}__options`}>
        <article>
          <TextField
            value={`${dateRangeField}`}
            className={`${blockName}__input ${blockName}--large-input`}
            variant="outlined"
            id="date"
            placeholder="yyyy-MM-dd - yyyy-MM-dd"
            InputProps={{
              endAdornment: (
                <InputAdornment>
                  <CalendarToday />
                </InputAdornment>
              ),
            }}
            onFocus={handleOpenModal}
          />

          <div
            ref={dateMenuRef}
            id="date-menu"
            className={clsx(
              `${blockName}__date-menu`,
              !isMenuOpen && `${blockName}__hidden-menu`
            )}
          >
            <div className={`${blockName}__menu-content`}>
              <article className={`${blockName}__menu_row`}>
                <p onClick={handleOpenDatePicker}>Custom</p>
              </article>
              {isDatePickerOpen && (
                <article
                  className={`${blockName}__date-range`}
                  data-testid={`${blockName}__date-range`}
                >
                  <div>
                    <DayPicker
                      disabledDays={{
                        after: new Date(),
                      }}
                      numberOfMonths={2}
                      toMonth={dateRange.to || new Date()}
                      selectedDays={[
                        dateRange.from,
                        {
                          from: dateRange.from,
                          to: dateRange.to,
                        },
                      ]}
                      modifiers={{
                        start: dateRange.from,
                        end: dateRange.to,
                      }}
                      onDayClick={handleDateRange}
                    />
                  </div>
                  <div className={`${blockName}__date-range-footer`}>
                    <div className={`${blockName}__date-range-footer-dates`}>
                      <span>
                        {dateRange && dateRange.from
                          ? moment(dateRange.from).format(DATE_FORMAT)
                          : ''}
                      </span>
                      <span>to</span>
                      <span>
                        {dateRange && dateRange.to
                          ? moment(dateRange.to).format(DATE_FORMAT)
                          : ''}
                      </span>
                    </div>
                    <article>
                      <Button
                        onClick={handleCloseDatePicker}
                        className={`${blockName}__filter-button`}
                      >
                        Cancel
                      </Button>
                      <Button
                        className={`${blockName}__filter-button`}
                        kind="primary"
                        onClick={onDateRangeSelected}
                      >
                        Apply
                      </Button>
                    </article>
                  </div>
                </article>
              )}
              <article className={`${blockName}__menu_row`}>
                <p id="last-month" onClick={onDateRangeSelected}>
                  Last 31 days
                </p>
              </article>
              <article className={`${blockName}__menu_row`}>
                <p id="current-month" onClick={onDateRangeSelected}>
                  Current Month
                </p>
              </article>
              <article className={`${blockName}__menu_row`}>
                <p id="previous-month" onClick={onDateRangeSelected}>
                  Previous Month
                </p>
              </article>
              <article className={`${blockName}__menu_row`}>
                <p id="last-three-months" onClick={onDateRangeSelected}>
                  Last three months
                </p>
              </article>
            </div>
          </div>
        </article>
        <TextField
          className={`${blockName}__input ${blockName}--large-input`}
          variant="outlined"
          id="invoice-name"
          value={invoiceName}
          onChange={onInvoiceNameChange}
          placeholder="Invoice Name"
          InputProps={{
            startAdornment: (
              <InputAdornment>
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </div>
    </article>
  );
};

InvoicesFilter.propTypes = {
  startAddInvoices: PropTypes.func.isRequired,
  setDateFilter: PropTypes.func.isRequired,
  setInvoiceNameFilter: PropTypes.func.isRequired,
  addGlobalWarning: PropTypes.func.isRequired,
  billToSiteNumber: PropTypes.string.isRequired,
};

export default InvoicesFilter;
